/**
 * Client-side helpers for ground-facing slab mode.
 */

export const DEFAULT_GROUND_SLAB_FOUNDATION = {
  code: 'GROUND_FACING_SLAB',
  name: 'Ground-facing Slab',
  foundation_type: 'ground_facing_slab',
  dimensions: { L: 6.0, W: 6.0, H: 0.25 },
  concrete: 'C30/37',
  pile_type: 'RTB',
  pile_size: 250,
  layout: [],
  num_piles: 0,
  rebar: '',
};

export const DEFAULT_SLAB_OPTIONS = {
  gravel: { enabled: true, thickness_m: 0.3, E_mpa: 80, gamma_kn_m3: 19, phi_deg: 38 },
  xps: { enabled: false, thickness_m: 0.1, E_mpa: 25 },
  pile_grid: {
    enabled: false,
    spacing_x_m: 2.5,
    spacing_y_m: 2.5,
    offset_x_m: 0.5,
    offset_y_m: 0.5,
    pile_type: 'RTB',
    pile_size_mm: 250,
    pile_length_m: 12.0,
  },
  point_load_kn: 50, // legacy
  loads: {
    uniform_enabled: true,
    uniform_live_kpa: 2.0,
    concentrated: {
      enabled: false,
      force_kn: 20,
      patch_mm: 200,
    },
    line: {
      enabled: false,
      intensity_kn_per_m: 5,
      edges: ['pos_x'],
    },
    edge_inset_m: 0.3,
  },
  mesh: { spacing_m: 1 },
  cover_m: 0.05,
  design_basis: { gamma_g: 1.35, gamma_q: 1.5 },
  loadsVersion: 2,
};

/** Node counts for a target element size. The plate solver needs at least 5 nodes each way. */
export function meshNodeCounts(lengthM, widthM, spacingM) {
  const h = Math.max(Number(spacingM) || 1, 0.2);
  const nx = Math.max(5, Math.round(Number(lengthM) / h) + 1);
  const ny = Math.max(5, Math.round(Number(widthM) / h) + 1);
  return { spacing_m: h, nx, ny };
}

export function generateRegularPileGrid(lengthM, widthM, pileGrid) {
  if (!pileGrid?.enabled) return [];
  const halfL = lengthM / 2;
  const halfW = widthM / 2;
  const layout = [];
  const sx = Number(pileGrid.spacing_x_m) || 2.5;
  const sy = Number(pileGrid.spacing_y_m) || 2.5;
  const ox = Number(pileGrid.offset_x_m) || 0.5;
  const oy = Number(pileGrid.offset_y_m) || 0.5;
  const len = Number(pileGrid.pile_length_m) || 12;
  for (let x = -halfL + ox; x <= halfL - ox + 1e-9; x += sx) {
    for (let y = -halfW + oy; y <= halfW - oy + 1e-9; y += sy) {
      layout.push([
        Math.round(x * 10000) / 10000,
        Math.round(y * 10000) / 10000,
        0,
        0,
        len,
      ]);
    }
  }
  return layout;
}

/**
 * Simple mode uses the uniform live load as the point load (kN/m² → kN).
 * Above 2 kN the contact patch is 200×200 mm; at 2 kN or less it is 50×50 mm.
 */
export function automaticPointLoadFromUdl(uniformLiveKpa) {
  const force = Math.max(Number(uniformLiveKpa) || 0, 0);
  return {
    force_kn: force,
    patch_mm: force > 2 ? 200 : 50,
  };
}

/** Loads actually analysed. Hidden advanced concentrated and line loads are not sent in simple mode. */
export function slabLoadsForAnalysis(loads, { advanced = false } = {}) {
  if (advanced) {
    return { ...(loads || {}), load_mode: 'advanced' };
  }
  const uniformEnabled = loads?.uniform_enabled !== false;
  const q = uniformEnabled ? Number(loads?.uniform_live_kpa) || 0 : 0;
  const point = automaticPointLoadFromUdl(q);
  return {
    ...(loads || {}),
    load_mode: 'simple',
    uniform_enabled: uniformEnabled,
    concentrated: {
      enabled: point.force_kn > 0,
      force_kn: point.force_kn,
      patch_mm: point.patch_mm,
    },
    line: {
      ...(loads?.line || {}),
      enabled: false,
    },
    edge_inset_m: 0.3,
  };
}

function roundDepth(value) {
  return Math.round(Number(value) * 1000) / 1000;
}

function supportLayerDefaults(role) {
  if (role === 'xps') {
    return {
      soil_type: 'xps',
      support_role: 'xps',
      unit_weight: 0.4,
      cohesion: 0,
      friction_angle: 0,
      E_modulus: 25,
      modulus: 25,
      is_settling: false,
      N60: '',
      fines_content_pct: '',
      is_saturated: false,
    };
  }
  return {
    soil_type: 'gravel',
    support_role: 'gravel',
    unit_weight: 19,
    cohesion: 0,
    friction_angle: 38,
    E_modulus: 80,
    modulus: 80,
    is_settling: false,
    N60: '',
    fines_content_pct: '',
    is_saturated: false,
  };
}

function isSupportLayer(layer) {
  return layer?.support_role === 'gravel' || layer?.support_role === 'xps';
}

/** Drop gravel/XPS cap layers and shift the remaining profile back to the surface. */
export function stripSupportLayers(soilLayers) {
  const natural = (soilLayers || []).filter((layer) => !isSupportLayer(layer));
  let cursor = 0;
  return natural.map((layer) => {
    const thick = Math.max(0, Number(layer.bottom_depth) - Number(layer.top_depth));
    const next = {
      ...layer,
      top_depth: roundDepth(cursor),
      bottom_depth: roundDepth(cursor + thick),
    };
    cursor = next.bottom_depth;
    return next;
  });
}

/**
 * Place enabled XPS and gravel on top of the site soil profile.
 * Order from the slab soffit: XPS, then gravel, then the existing layers.
 */
export function applySupportLayersToProfile(soilLayers, slabOptions) {
  const natural = (soilLayers || []).filter((layer) => !isSupportLayer(layer));
  const caps = [];
  if (slabOptions?.xps?.enabled) {
    const thickness = Number(slabOptions?.xps?.thickness_m ?? 0.1);
    if (thickness > 0) caps.push({ role: 'xps', thickness });
  }
  if (slabOptions?.gravel?.enabled !== false) {
    const thickness = Number(slabOptions?.gravel?.thickness_m);
    if (thickness > 0) caps.push({ role: 'gravel', thickness });
  }

  let cursor = 0;
  const head = caps.map(({ role, thickness }) => {
    const previous = (soilLayers || []).find((layer) => layer.support_role === role);
    const layer = {
      ...(previous || supportLayerDefaults(role)),
      ...supportLayerDefaults(role),
      ...(previous
        ? {
          unit_weight: previous.unit_weight,
          cohesion: previous.cohesion,
          friction_angle: previous.friction_angle,
          E_modulus: previous.E_modulus,
          modulus: previous.modulus ?? previous.E_modulus,
        }
        : {}),
      soil_type: role === 'xps' ? 'xps' : 'gravel',
      support_role: role,
      top_depth: roundDepth(cursor),
      bottom_depth: roundDepth(cursor + thickness),
    };
    cursor = layer.bottom_depth;
    return layer;
  });

  const shifted = natural.map((layer) => {
    const thick = Math.max(0, Number(layer.bottom_depth) - Number(layer.top_depth));
    const next = {
      ...layer,
      top_depth: roundDepth(cursor),
      bottom_depth: roundDepth(cursor + thick),
    };
    cursor = next.bottom_depth;
    return next;
  });
  return [...head, ...shifted];
}

export function supportProfilesEqual(a, b) {
  if ((a || []).length !== (b || []).length) return false;
  return (a || []).every((layer, index) => {
    const other = b[index];
    return layer.soil_type === other.soil_type
      && layer.support_role === other.support_role
      && Number(layer.top_depth) === Number(other.top_depth)
      && Number(layer.bottom_depth) === Number(other.bottom_depth)
      && Number(layer.E_modulus) === Number(other.E_modulus)
      && Number(layer.unit_weight) === Number(other.unit_weight)
      && Number(layer.friction_angle) === Number(other.friction_angle);
  });
}

export function buildSlabFoundationWithLayout(foundation, slabOptions) {
  const dims = foundation?.dimensions || { L: 6, W: 6, H: 0.25 };
  const layout = generateRegularPileGrid(dims.L, dims.W, slabOptions?.pile_grid);
  return {
    ...foundation,
    layout,
    num_piles: layout.length,
    pile_type: slabOptions?.pile_grid?.pile_type || 'RTB',
    pile_size: slabOptions?.pile_grid?.pile_size_mm || 250,
  };
}
